import { Button, FoodsDeleteButton, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAllIngredientReferences } from "src/api/client-api/ingredientReference/useAllIngredientReferences";
import {
    AddItemField,
    FormFieldLoadError,
    FormItemRow,
    SectionCard,
    SectionContent,
    SectionHeader,
} from "src/components/EntityForm";
import {
    IngredientSupplierTransportControl,
    IngredientSupplierTransportDialog,
} from "src/components/Transport/IngredientSupplierTransportFields";
import { getNextIngredientSupplierTransportStatus } from "src/components/Transport/ingredientSupplierTransportPersistence";
import type { IngredientSupplierTransportChangeReason } from "src/components/Transport/ingredientSupplierTransportTypes";

import type {
    IngredientFormIngredientCompany,
    IngredientFormIngredientReferenceLine,
    IngredientSupplierTransportSelection,
} from "../../IngredientForm.types";
import { CompanyModal } from "../../Pages/Page2/CompanyModal/CompanyModal";
import { IngredientSupplierReferenceField } from "../../Pages/Page2/IngredientSupplierReferenceField";
import { getSupplierMutationPermission } from "../../Pages/Page2/supplierMutationPermission";

type IngredientSuppliersSectionProps = {
    suppliers: IngredientFormIngredientCompany[];
    onChange: (suppliers: IngredientFormIngredientCompany[]) => void;
    progress: { current: number; total: number };
    showTransport: boolean;
    canModify?: boolean;
};

const extractIdFromIri = (iri?: IRI): number | undefined => {
    const rawId = iri?.split("/").pop();
    const id = rawId === undefined ? Number.NaN : Number.parseInt(rawId, 10);
    return Number.isNaN(id) ? undefined : id;
};

/** Ingredient-owned supplier rows matching Figma `12819:70148` and `12819:69535`. */
export const IngredientSuppliersSection = ({
    suppliers,
    onChange,
    progress,
    showTransport,
    canModify = true,
}: IngredientSuppliersSectionProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [selectorOpen, setSelectorOpen] = useState(false);
    const [replacingSupplierIri, setReplacingSupplierIri] = useState<IRI | null>(null);
    const [transportRow, setTransportRow] = useState<IngredientFormIngredientCompany | null>(null);
    const {
        ingredientReferences,
        isError: referencesError,
        isLoading: referencesLoading,
        refetch: refetchReferences,
    } = useAllIngredientReferences({ fetchAllPages: true });
    const referenceOptions = useMemo<IngredientFormIngredientReferenceLine[]>(
        () =>
            (ingredientReferences ?? []).map((reference) => ({
                "@id": reference["@id"],
                id: reference.id,
                name: reference.name,
            })),
        [ingredientReferences],
    );

    const canMutate = getSupplierMutationPermission(canModify) === "allowed";

    const openSelector = (supplierIri: IRI | null = null) => {
        setReplacingSupplierIri(supplierIri);
        setSelectorOpen(true);
    };
    const removeSupplier = (supplierIri: IRI) =>
        onChange(suppliers.filter((row) => row.supplier["@id"] !== supplierIri));
    const updateReference = (supplierIri: IRI, ingredientReference: IngredientFormIngredientReferenceLine | null) =>
        onChange(suppliers.map((row) => (row.supplier["@id"] === supplierIri ? { ...row, ingredientReference } : row)));
    const updateTransport = (
        supplierIri: IRI,
        transportSelections: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ) =>
        onChange(
            suppliers.map((row) =>
                row.supplier["@id"] === supplierIri ?
                    {
                        ...row,
                        transportSelections,
                        transportSelectionsStatus: getNextIngredientSupplierTransportStatus(
                            row.transportSelectionsStatus,
                            reason,
                        ),
                    }
                :   row,
            ),
        );

    return (
        <SectionCard>
            <SectionHeader
                progress={progress}
                title={t("ingredient.suppliers.title", "Suppliers")}
                subtitle={t(
                    "ingredient.form.suppliers.sectionSubtitleV2",
                    "Add the different suppliers of your ingredients.",
                )}
            />
            <SectionContent>
                <CompanyModal
                    appearance="product-v2"
                    canModify={canModify}
                    existingIngredientSuppliers={suppliers}
                    isOpen={selectorOpen}
                    replacingSupplierIri={replacingSupplierIri}
                    selectionMode="single"
                    setOpen={setSelectorOpen}
                    onValidate={onChange}
                />

                {referencesError && (
                    <FormFieldLoadError
                        message={t(
                            "ingredient.form.suppliers.referencesError",
                            "Ingredient references could not be loaded.",
                        )}
                        retryLabel={t("common.actions.refresh", "Refresh")}
                        onRetry={refetchReferences}
                    />
                )}

                <Stack gap={theme.spacing(3)}>
                    {suppliers.map((row) => {
                        const supplierIri = row.supplier["@id"];

                        return (
                            <FormItemRow
                                density="compact"
                                key={row["@id"] ?? supplierIri}
                                testId="ingredient-supplier-row"
                            >
                                <Box
                                    sx={{
                                        alignItems: "start",
                                        columnGap: theme.spacing(2),
                                        display: "grid",
                                        gridTemplateColumns:
                                            showTransport ?
                                                {
                                                    xs: "minmax(0, 1fr)",
                                                    md: `minmax(${theme.spacing(22.5)}, 0.75fr) minmax(${theme.spacing(35)}, 1.25fr)`,
                                                    xl: `minmax(${theme.spacing(22.5)}, 0.7fr) minmax(${theme.spacing(35)}, 1.3fr) minmax(${theme.spacing(32.5)}, 1fr) auto`,
                                                }
                                            :   {
                                                    xs: "minmax(0, 1fr)",
                                                    md: `minmax(${theme.spacing(22.5)}, 0.75fr) minmax(${theme.spacing(35)}, 1.25fr) auto`,
                                                },
                                        minWidth: 0,
                                        rowGap: theme.spacing(1.5),
                                        width: "100%",
                                    }}
                                >
                                    <Stack minWidth={0} gap={theme.spacing(0.5)}>
                                        <Typography variant="small-semibold" sx={{ color: theme.custom.grey[2000] }}>
                                            {t("ingredient.form.suppliers.supplierColumn", "Supplier")}
                                        </Typography>
                                        <Button
                                            aria-label={row.supplier.name}
                                            disabled={!canMutate}
                                            onClick={() => canMutate && openSelector(supplierIri)}
                                            type="button"
                                            variant="text"
                                            sx={{
                                                alignSelf: "flex-start",
                                                color: theme.palette.primary[700],
                                                fontWeight: 700,
                                                height: "auto",
                                                justifyContent: "flex-start",
                                                maxWidth: "100%",
                                                minWidth: 0,
                                                overflow: "hidden",
                                                padding: 0,
                                                textAlign: "left",
                                                textOverflow: "ellipsis",
                                                textTransform: "none",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {row.supplier.name}
                                        </Button>
                                        <Typography
                                            variant="small-regular"
                                            sx={{ color: theme.custom.grey[2000] }}
                                            noWrap
                                        >
                                            {row.supplier.clientId ?? t("common.not_available", "Not available")}
                                        </Typography>
                                    </Stack>

                                    <Stack gap={theme.spacing(0.75)} minWidth={0}>
                                        <Typography variant="small-semibold" sx={{ color: theme.custom.grey[2000] }}>
                                            {t(
                                                "ingredient.form.suppliers.ingredientReferenceColumn",
                                                "Ingredient reference (override)",
                                            )}
                                        </Typography>
                                        {canMutate && !referencesError ?
                                            <IngredientSupplierReferenceField
                                                appearance="product-v2"
                                                isLoading={referencesLoading}
                                                options={referenceOptions}
                                                value={row.ingredientReference ?? null}
                                                onChange={(reference) => updateReference(supplierIri, reference)}
                                            />
                                        :   <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                                                {row.ingredientReference?.name ??
                                                    t(
                                                        "ingredient.form.suppliers.ingredientReferencePlaceholder",
                                                        "No override",
                                                    )}
                                            </Typography>
                                        }
                                    </Stack>

                                    {showTransport && (
                                        <Stack gap={theme.spacing(0.75)} minWidth={0}>
                                            <Typography
                                                variant="small-semibold"
                                                sx={{ color: theme.custom.grey[2000] }}
                                            >
                                                {t("ingredient.form.suppliers.transportColumn", "Transport")}
                                            </Typography>
                                            <IngredientSupplierTransportControl
                                                appearance="product-v2"
                                                disabled={!canMutate}
                                                ingredientSupplierId={extractIdFromIri(row["@id"])}
                                                summaryVariant="inline"
                                                value={row.transportSelections ?? {}}
                                                onConfigure={() => canMutate && setTransportRow(row)}
                                            />
                                        </Stack>
                                    )}

                                    <Box sx={{ alignSelf: "center", justifySelf: "end" }}>
                                        <FoodsDeleteButton
                                            ariaLabel={`${t("common.actions.delete", "Delete")} ${row.supplier.name}`}
                                            disabled={!canMutate}
                                            onClick={() => canMutate && removeSupplier(supplierIri)}
                                            buttonProps={{ type: "button" }}
                                            tooltip={{ text: t("common.actions.delete", "Delete") }}
                                        />
                                    </Box>
                                </Box>
                            </FormItemRow>
                        );
                    })}

                    <AddItemField
                        actionLabel={t("ingredient.form.suppliermodal.title", "Add a supplier")}
                        description={
                            suppliers.length === 0 ?
                                t(
                                    "ingredient.form.suppliers.emptyDescriptionV2",
                                    "Add a first supplier to your product.",
                                )
                            :   undefined
                        }
                        onAction={() => openSelector()}
                    />
                </Stack>

                {showTransport && transportRow !== null && (
                    <IngredientSupplierTransportDialog
                        appearance="product-v2"
                        open
                        onClose={() => setTransportRow(null)}
                        title={transportRow.supplier.name}
                        ingredientSupplierId={extractIdFromIri(transportRow["@id"])}
                        value={transportRow.transportSelections ?? {}}
                        saveMode="deferred"
                        onChange={(next, reason) => updateTransport(transportRow.supplier["@id"], next, reason)}
                    />
                )}
            </SectionContent>
        </SectionCard>
    );
};
